import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';
import { KPI_COLUMNS, CORE_KPI_COLUMNS, defaultDateRange } from '../utils/vendorKpi';
import { DateFilters, ScoreTrendChart, StatusIcon, KpiValueCell } from '../components/vendorMgmt/VendorKpiUi';

export default function VendorScore() {
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.start_date);
  const [endDate, setEndDate] = useState(defaults.end_date);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const res = await api.get(`/vendors/score?${qs}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    dispatch({ type: 'SET_BREADCRUMB', payload: ['Home', 'Vendor Management', 'Vendor score'] });
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const score = data?.aggregate_score ?? 0;
  const breakdown = data?.breakdown || [];

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-lg font-bold text-sap-text">Vendor score</h1>
        <DateFilters
          showKpiGroup={false}
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
          kpiGroup="All"
          kpiGroups={[]}
          onKpiGroupChange={() => {}}
        />
      </div>

      {loading && !data ? (
        <div className="py-16 text-center text-sm text-sap-textLight">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
              <div className="bg-white rounded-xl border-2 border-emerald-200 shadow-sm p-4">
                <h3 className="text-xs font-semibold text-sap-text mb-2">Vendor score</h3>
                <div className="flex items-center gap-2">
                  <StatusIcon value={score} />
                  <span className="text-3xl font-light text-sap-text">{Number(score).toFixed(2)}%</span>
                </div>
                <p className="text-[11px] text-sap-textLight mt-2">
                  {data?.score_change >= 0 ? '+' : ''}{Number(data?.score_change || 0).toFixed(2)}% ({Number(data?.score_change_pct || 0).toFixed(1)}%) since prior period
                </p>
                <p className="text-[10px] text-sap-textLight mt-1">Data through {endDate}</p>
              </div>
              <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4">
                <h3 className="text-xs font-semibold text-sap-text mb-2">Performance</h3>
                <ScoreTrendChart points={data?.trend || []} />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-sap-border/60">
                <h3 className="text-sm font-semibold text-sap-text">Vendors breakdown ({breakdown.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                  <thead>
                    <tr className="bg-[#F7F5F2] text-[11px] uppercase text-sap-textLight">
                      <th className="px-4 py-2 text-left font-semibold">Vendor</th>
                      {CORE_KPI_COLUMNS.map((col) => (
                        <th key={col.key} className="px-4 py-2 text-right font-semibold">{col.label}</th>
                      ))}
                    </tr>
                    <tr className="bg-[#F7F5F2] text-[11px] text-sap-textLight">
                      <td className="px-4 py-1.5 font-semibold">Weight</td>
                      {CORE_KPI_COLUMNS.map((col) => (
                        <td key={col.key} className="px-4 py-1.5 text-right">{data?.weights?.[col.key] ?? 25}%</td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.map((row) => (
                      <tr
                        key={row.vendor_id}
                        className="border-t border-sap-border/40 hover:bg-sap-blue/5 cursor-pointer"
                        onClick={() => navigate(`/vendor-management/vendor/${row.vendor_id}`)}
                      >
                        <td className="px-4 py-2.5 font-medium text-sap-blue">{row.vendor_name}</td>
                        {CORE_KPI_COLUMNS.map((col) => (
                          <td key={col.key} className="px-4 py-2.5 text-right">
                            <KpiValueCell value={row[col.key]} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4 h-fit">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-sap-text">Targets</h3>
            </div>
            <div className="flex gap-2 mb-4 text-[11px]">
              <span className="px-2 py-1 rounded bg-sap-blue/10 text-sap-blue font-semibold">For everyone</span>
            </div>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] text-sap-textLight">Indicator</dt>
                <dd className="font-medium text-sap-text">Vendor Score</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Status</dt>
                <dd className="flex items-center gap-1.5 font-medium">
                  {data?.target_met ? (
                    <>
                      <StatusIcon value={90} />
                      <span className="text-emerald-700">Met</span>
                    </>
                  ) : (
                    <>
                      <StatusIcon value={50} />
                      <span className="text-amber-700">Below target</span>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Target</dt>
                <dd className="font-medium">{data?.target_score ?? 80}%</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Final score</dt>
                <dd className="font-medium">{Math.round(score)}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Start date</dt>
                <dd>{startDate}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Review date</dt>
                <dd>{endDate}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
